/* import __COLOCATED_TEMPLATE__ from './teammate-email-reply.hbs'; */
/* RESPONSIBLE TEAM: team-channels */
import Component from '@glimmer/component';
import type PartGroup from 'embercom/objects/inbox/conversation-stream/part-group';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { Channel } from 'embercom/models/data/inbox/channels';
import type Conversation from 'embercom/objects/inbox/conversation';
import type AdminComment from 'embercom/objects/inbox/renderable/admin-comment';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type ConversationTranslationSettings from 'embercom/services/conversation-translation-settings';
import {
  getAllTranslationLanguageCodesFromPartGroup,
  getOriginalLanguageCodeFromPartGroup,
  getLanguageLabelFromCode,
  isTranslatingPartGroup,
  translatedPartExists,
  hasTranslationStatusInPartGroup,
} from '../helpers/translations';
import {
  normalizeLocale,
  nextInCycle,
  getDefaultWorkspaceLocale,
} from '../helpers/language-selection';
import {
  INBOUND_EMAIL_SUPPORTED_TAGS,
  INBOUND_EMAIL_SUPPORTED_ATTRIBUTES,
} from 'embercom/lib/channels/constants';
import { computeTranslationUi } from '../helpers/translation-ui';
import type IntlService from 'ember-intl/services/intl';

interface TeammateEmailReplyRenderablePart extends RenderablePart {
  renderableData: AdminComment;
}

interface TeammateEmailReplyPartGroup extends PartGroup {
  parts: Array<TeammateEmailReplyRenderablePart>;
}

interface Args {
  partGroup: TeammateEmailReplyPartGroup;
  conversation: Conversation;
  isReadOnly: boolean;
}

interface Signature {
  Args: Args;
}

export default class Inbox2ConversationStreamPartGroupsTeammateEmailReplyComponent extends Component<Signature> {
  @service declare session: Session;
  @service declare conversationTranslationSettings: ConversationTranslationSettings;
  @service declare intl: IntlService;
  @service declare frontendStatsService: any;

  supportedTags = INBOUND_EMAIL_SUPPORTED_TAGS;
  supportedAttributes = INBOUND_EMAIL_SUPPORTED_ATTRIBUTES;

  @tracked selectedLangCode?: string;

  get availableTranslationCodes(): string[] {
    return getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup);
  }

  get originalLanguageCode(): string | undefined {
    return getOriginalLanguageCodeFromPartGroup(this.args.partGroup);
  }

  get defaultLanguageCode(): string | undefined {
    return getDefaultWorkspaceLocale(this.session);
  }

  private get translationUi() {
    return computeTranslationUi({
      session: this.session,
      available: this.availableTranslationCodes,
      preferred: this.session?.teammate?.teammatePreferredLocale,
      defaultLang: this.defaultLanguageCode,
      original: this.originalLanguageCode,
      selectedLangCode:
        this.conversationTranslationSettings.languageOverrideForConversation(
          this.args.conversation.id,
        ) ?? this.selectedLangCode,
    });
  }

  get cycleOrderCodes(): string[] {
    return this.translationUi.cycleOrderCodes;
  }

  get currentSelectedCode(): string | undefined {
    return this.translationUi.currentSelectedCode;
  }

  get selectedTranslationLanguageCode(): string | undefined {
    if (!this.autoTranslationEnabled && !this.args.isReadOnly) {
      return undefined;
    }
    return this.currentSelectedCode;
  }

  get teammate(): AdminSummary {
    return this.args.partGroup.parts[0].renderableData.adminSummary;
  }

  get emailChannel() {
    return Channel.Email;
  }

  get lastPartSeenByUser() {
    let { conversation } = this.args;

    return (
      conversation?.lastSeenByUserAt &&
      conversation?.lastSeenByUserAt >= this.lastPartInGroup.createdAt
    );
  }

  get lastPartInGroup() {
    let { parts } = this.args.partGroup;
    return parts[parts.length - 1];
  }

  get autoTranslationEnabled() {
    return this.conversationTranslationSettings.autoTranslationEnabledForConversationObject(
      this.args.conversation,
    );
  }

  get showOriginalMessage(): boolean {
    if (!this.translatedPartExists) {
      return true;
    }
    // In read-only mode, use the cycle state
    if (this.args.isReadOnly) {
      return (
        normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode)
      );
    }
    // In normal mode, require auto translation to be enabled
    if (!this.autoTranslationEnabled) {
      return true;
    }
    return normalizeLocale(this.currentSelectedCode) === normalizeLocale(this.originalLanguageCode);
  }

  get showTranslatedMessage() {
    return !this.showOriginalMessage;
  }

  get translatedPartExists() {
    return translatedPartExists(this.args.partGroup);
  }

  get isTranslating() {
    return isTranslatingPartGroup(this.args.partGroup);
  }

  get conversationLanguageCode(): string | undefined {
    let raw = this.args.conversation?.getAttributeForDescriptor('language')?.value as
      | string
      | undefined;
    return normalizeLocale(typeof raw === 'string' ? raw : undefined);
  }

  get conversationLanguage(): string {
    // Try conversation language attribute first, then fall back to block translations, then workspace locale
    let code =
      this.conversationLanguageCode ||
      getAllTranslationLanguageCodesFromPartGroup(this.args.partGroup)[0] ||
      this.session.workspace?.locale;
    return code ? getLanguageLabelFromCode(code, this.intl.primaryLocale) || '' : '';
  }

  get showTranslationToggle() {
    // Always show if there's a translation status (even if translation failed or was filtered)
    if (hasTranslationStatusInPartGroup(this.args.partGroup)) {
      return true;
    }

    // In read-only mode, show translation toggle if translated content exists
    if (this.args.isReadOnly) {
      return this.translatedPartExists;
    }

    let baseVisible =
      this.autoTranslationEnabled && (this.translatedPartExists || this.isTranslating);
    if (!baseVisible) {
      return false;
    }

    if (this.session.workspace.isAdHocTranslationsEnabled) {
      if (!this.adHocTarget) {
        return false;
      }
    }

    return true;
  }

  private get adHocTarget(): string | undefined {
    let normPreferred = normalizeLocale(this.session.teammate?.teammatePreferredLocale);
    let normOriginal = normalizeLocale(this.originalLanguageCode);
    let hasPreferred = this.availableTranslationCodes.some(
      (c) => normalizeLocale(c) === normPreferred,
    );
    if (normPreferred && normOriginal !== normPreferred && hasPreferred) {
      return normPreferred;
    }
    return this.availableTranslationCodes
      .map((c) => normalizeLocale(c))
      .find((c) => c !== normOriginal);
  }

  get selectedLanguageLabel(): string {
    let code = this.currentSelectedCode;
    if (!code) {
      return '';
    }
    return getLanguageLabelFromCode(code, this.intl.primaryLocale) || '';
  }

  get translatedLanguageCode(): string | undefined {
    if (this.conversationLanguageCode) {
      return this.conversationLanguageCode;
    }
    let original = normalizeLocale(this.originalLanguageCode);
    return (
      this.cycleOrderCodes.find((c) => c !== original) ||
      this.defaultLanguageCode ||
      this.session.workspace?.locale
    );
  }

  get showTranslationQualityFeedback() {
    return this.session.workspace.isFeatureEnabled('translation-quality-feedback');
  }

  get currentTranslationAudience(): 'inbound' | 'outbound' {
    let current = normalizeLocale(this.currentSelectedCode);
    let customerCode = normalizeLocale(this.translatedLanguageCode);
    if (customerCode && current === customerCode) {
      return 'outbound';
    }
    return 'inbound';
  }

  @action recordEmailRendered(): void {
    this.frontendStatsService.enqueueInteractionMetric({
      name: 'email_rendered',
      email_direction: 'outbound_teammate',
    });
    this.frontendStatsService.sendInteractionMetrics();
  }

  @action toggleTranslation() {
    this.conversationTranslationSettings.clearLanguageOverride();

    if (this.session.workspace.isAdHocTranslationsEnabled) {
      let target = this.adHocTarget;
      if (!target) {
        return;
      }
      let normOrig = normalizeLocale(this.originalLanguageCode);
      this.selectedLangCode =
        normalizeLocale(this.currentSelectedCode) === target ? (normOrig ?? target) : target;
      return;
    }
    let next = nextInCycle(this.currentSelectedCode, this.cycleOrderCodes);
    if (next) {
      this.selectedLangCode = next;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::PartGroups::TeammateEmailReply': typeof Inbox2ConversationStreamPartGroupsTeammateEmailReplyComponent;
    'inbox2/conversation-stream/part-groups/teammate-email-reply': typeof Inbox2ConversationStreamPartGroupsTeammateEmailReplyComponent;
  }
}
